const formatDateTime = dateTime => {
  const dateToFormat = dateTime && new Date(dateTime);
  const date = dateToFormat?.toLocaleDateString();
  const time = dateToFormat?.toLocaleTimeString();
  return {
    date,
    time
  };
};

const formatDate = date => {
  const dateFormatted = date && new Date(date).toLocaleString();
  return dateFormatted;
};

const STORAGE_RESIDUE_THRESHOLD = 0.01;

const getPurchaseType = purchaseTypeId =>
  ({
    1: "Kg",
    2: "Un"
  }[purchaseTypeId]);

const hasMeaningfulBalance = value =>
  Math.abs(Number(value) || 0) >= STORAGE_RESIDUE_THRESHOLD;

const formatStorageQuantity = (value, purchaseTypeId) => {
  const quantity = Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(value) || 0);
  const purchaseType = getPurchaseType(purchaseTypeId);

  return `${quantity} ${purchaseType}`;
};

const formatStorageData = storageData => {
  return (
    (storageData || [])
      // Saldos ínfimos (|saldo| < 0,01) são resíduos, não estoque real — ficam
      // fora da tabela, do card "Estoque Negativo" e do relatório
      ?.filter(storage => hasMeaningfulBalance(storage.summary))
      .map(storage => {
        return {
          ...storage,
          quantity: formatStorageQuantity(
            storage.summary,
            storage.purchase_type
          )
        };
      })
      .sort((a, b) => a.summary - b.summary)
  );
};

const buildStorageBalanceRow = (currentStorage, futureStorage) => {
  const baseStorage = currentStorage || futureStorage;
  const futureSummary = futureStorage?.summary ?? currentStorage?.summary ?? 0;
  const anticipatedScheduledDebits =
    futureStorage?.anticipated_scheduled_debits ?? 0;
  const currentSummary =
    currentStorage?.summary ?? futureSummary - anticipatedScheduledDebits;

  return {
    ...baseStorage,
    summary: currentSummary,
    quantity: formatStorageQuantity(currentSummary, baseStorage.purchase_type),
    currentSummary,
    futureSummary,
    currentQuantity: formatStorageQuantity(
      currentSummary,
      baseStorage.purchase_type
    ),
    futureQuantity: formatStorageQuantity(
      futureSummary,
      baseStorage.purchase_type
    )
  };
};

const formatMergedStorageData = (currentStorageData, futureStorageData) => {
  const currentStorageMap = new Map();
  const futureStorageMap = new Map();

  (currentStorageData || [])
    .filter(storage => hasMeaningfulBalance(storage.summary))
    .forEach(storage => {
      currentStorageMap.set(storage.product_base_id, storage);
    });

  (futureStorageData || []).forEach(storage => {
    futureStorageMap.set(storage.product_base_id, storage);
  });

  const productBaseIds = new Set(currentStorageMap.keys());

  futureStorageMap.forEach(storage => {
    const futureSummary = Number(storage.summary) || 0;
    if (futureSummary <= 0 && hasMeaningfulBalance(futureSummary)) {
      productBaseIds.add(storage.product_base_id);
    }
  });

  return Array.from(productBaseIds)
    .map(productBaseId =>
      buildStorageBalanceRow(
        currentStorageMap.get(productBaseId),
        futureStorageMap.get(productBaseId)
      )
    )
    .sort((a, b) => {
      const aWorstBalance = Math.min(a.currentSummary, a.futureSummary);
      const bWorstBalance = Math.min(b.currentSummary, b.futureSummary);

      if (aWorstBalance !== bWorstBalance) {
        return aWorstBalance - bWorstBalance;
      }

      return a.product_base_name.localeCompare(b.product_base_name);
    });
};

const formatBatchData = batchData => {
  // Ordena pelo tempo restante preciso (minutos), do mais urgente/vencido ao
  // mais distante. minutes_to_due preserva horas/minutos; days_to_dude (dias
  // truncados) e usado apenas como fallback caso o campo nao venha.
  const sortKey = batch =>
    batch.minutes_to_due ?? (batch.days_to_dude ?? 0) * 1440;

  const batches = (batchData || []).sort((a, b) => sortKey(a) - sortKey(b));

  const dueBatches = batches.reduce((acc, batch) => {
    return sortKey(batch) < 0 ? (acc += 1) : acc;
  }, 0);

  const batchesToDue = batches.length - dueBatches;
  return { batches, dueBatches, batchesToDue };
};

const formatSellerPendingData = data => {
  const result = data?.reduce(
    (acc, seller) => {
      const hasReceivable = seller.batches.some(batch => !batch.blitz_step);
      const hasPending = seller.batches.some(batch => batch.blitz_step > 0);

      hasReceivable && acc.suppliersReceivable++;
      hasPending && acc.suppliersPending++;

      return acc;
    },
    { suppliersReceivable: 0, suppliersPending: 0 }
  );

  return result || { suppliersReceivable: 0, suppliersPending: 0 };
};

const formatSellerReceiptData = data => {
  const finalizedSuppliers = data.length ?? 0;

  return { finalizedSuppliers };
};

const formatConfirmPendingData = data => {
  const unconfirmed = data.length ?? 0;

  return { unconfirmed };
};

const formatBatchBreakageData = data => {
  const batches = data.batches;
  const complaints = data.complaints;
  const batchBreakageQty = batches.length ?? 0;
  const batchData = (batches || [])
    .map(batch => {
      return {
        id: batch.id,
        classification: batch.classification,
        productBaseName: batch.product?.name,
        productBaseShelfLife: batch.product?.shelf_life,
        receiptDate: formatDateTime(batch.receipt_date).date,
        receiptTime: formatDateTime(batch.receipt_date).time,
        complaintReasons: batch.complaint_reasons
      };
    })
    .sort((a, b) => a.productBaseName.localeCompare(b.productBaseName));

  const complaintsData = (complaints || []).flatMap(complaint => {
    const complaintItems = (complaint.items || []).map(item => {
      const receiptDates = (item.batches || []).map(batch => {
        return new Date(batch?.created) > new Date(batch?.receipt_date)
          ? formatDate(batch?.created)
          : formatDate(batch?.receipt_date);
      });
      const receiptDatesString = receiptDates && receiptDates.join(" | ");

      const batchIds = (item.batches || []).map(batch => batch?.id);
      const batchIdsString = batchIds && batchIds.join(" | ");

      const batchClassifications = (item.batches || []).map(
        batch => batch?.classification
      );
      const batchClassificationsString =
        batchClassifications && batchClassifications.join(" | ");

      const formatPrePickingDate = prePickingDates => {
        if (prePickingDates) {
          const dates = prePickingDates.split("/");
          const formatted = dates.map(date => formatDate(date));
          return formatted.join(" | ");
        }
      };

      const itemComplaint = {
        orderId: complaint.order?.id,
        scheduleDate: formatDateTime(complaint.order?.scheduled_date).date,
        skuName: item.product?.name,
        reasonName: item.reason?.name,
        originalQuantity: item.quantity_original,
        complaintQuantity: item.quantity_complaint,
        ItemObservation: item.observation,
        itemImage: item.item_image,
        batchId: batchIdsString,
        batchClassification: batchClassificationsString,
        batchReceiptDate: receiptDatesString,
        prePickingCreated: formatPrePickingDate(item.prePickingCreated),
        shelfLife: item.batches?.[0]?.product?.shelf_life
      };
      return itemComplaint;
    });
    return complaintItems;
  });

  return { batchBreakageQty, batchData, complaintsData };
};

export const formatData = {
  formatStorageData,
  formatMergedStorageData,
  formatBatchData,
  formatSellerPendingData,
  formatSellerReceiptData,
  formatConfirmPendingData,
  formatBatchBreakageData
};
